import { parsePairInput } from '@gymtv/core';
import type { SupabaseClient } from '@supabase/supabase-js';

export type TvDevice = {
  id: string;
  name: string;
  platform: string | null;
  paired_at: string;
  last_seen_at: string | null;
};

export type ClaimResult = { ok: true; device: TvDevice } | { ok: false; error: string };

export async function claimTv(client: SupabaseClient, input: string, name: string): Promise<ClaimResult> {
  const code = parsePairInput(input);
  if (!code) return { ok: false, error: 'That doesn’t look like a TV code. It has 6 letters and numbers.' };
  const { data, error } = await client.rpc('claim_pairing', { p_code: code, p_name: name.trim() || 'TV' });
  if (error) return { ok: false, error: error.message.includes('too many') ? 'Too many attempts. Wait a few minutes.' : error.message };
  if (!data || !data.id) return { ok: false, error: 'Code not found or expired. Check the code on your TV.' };
  return { ok: true, device: data as TvDevice };
}

export async function listTvs(client: SupabaseClient): Promise<TvDevice[]> {
  const { data, error } = await client
    .from('tv_devices')
    .select('id,name,platform,paired_at,last_seen_at')
    .is('revoked_at', null)
    .order('paired_at', { ascending: false });
  if (error) throw error;
  return data as TvDevice[];
}

export async function revokeTv(client: SupabaseClient, id: string): Promise<void> {
  const { error } = await client.rpc('revoke_tv_device', { p_device_id: id });
  if (error) throw error;
}

/** "online" when the TV checked in within the last 3 minutes (it calls tv_whoami every minute). */
export function tvOnline(d: TvDevice, now = Date.now()): boolean {
  return !!d.last_seen_at && now - new Date(d.last_seen_at).getTime() < 3 * 60 * 1000;
}
